import React, { useState, useEffect } from "react";
import { Edit, Delete, Settings, X, Save } from "lucide-react";

const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => (
  <>
    {isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="bg-white rounded-lg p-4 max-w-xs w-full shadow-lg">
          <p className="text-gray-800 text-sm mb-4">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);

const EditModal = ({ isOpen, programGroup, onSave, onCancel, isLoading }) => {
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (isOpen && programGroup) {
      setEditValue(programGroup.programme_group_name || "");
    }
  }, [isOpen, programGroup]);

  const handleSave = () => {
    if (
      typeof editValue === "string" &&
      editValue.trim() &&
      editValue.trim() !== (programGroup?.programme_group_name || "")
    ) {
      onSave(editValue.trim());
    } else {
      onCancel();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={onCancel}
        >
          <div
            className="bg-white rounded-lg p-4 max-w-sm w-full mx-4 shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-md">
                  <Edit size={16} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Edit Program Group
                </h3>
              </div>
              <button
                onClick={onCancel}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                disabled={isLoading}
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Program Group Name
                </label>
                <input
                  type="text"
                  value={editValue || ""}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Enter program group name..."
                  disabled={isLoading}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Press Enter to save or Escape to cancel
                </p>
              </div>

              {programGroup ? (
                <div className="bg-gray-50 rounded-lg p-2 text-sm">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    University
                  </label>
                  <p className="text-gray-800">
                    {programGroup.university_name || "Not specified"}
                  </p>
                </div>
              ) : (
                <p className="text-red-600 text-xs">
                  No program group data available
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={onCancel}
                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={
                  isLoading ||
                  !editValue ||
                  !editValue.trim() ||
                  editValue.trim() ===
                    (programGroup?.programme_group_name || "")
                }
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-1.5"
              >
                {isLoading ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const rowsPerPageOptions = [10, 25, 50, "All"];

const AllProgramGroups = ({ theme }) => {
  const [programGroups, setProgramGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenu, setActionMenu] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [openGroupId, setOpenGroupId] = useState(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/catalog/programme-groups`,
          {
            headers: { 
              "Content-Type": "application/json", 
              Authorization: `Bearer ${localStorage.getItem("token")}` 
            }
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch program groups");
        }
        const data = await response.json();
        setProgramGroups(data);
      } catch (err) {
        setToast({ type: "error", message: err.message });
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenu && !event.target.closest("[data-action-menu]")) {
        setActionMenu(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [actionMenu]);

  const filteredData = programGroups.filter((pg) =>
    (pg.programme_group_name ?? "")
      .toLowerCase()
      .includes((searchTerm ?? "").toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(totalItems / rowsPerPage);

  const displayedData =
    rowsPerPage === "All"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage
        );

  const getPageNumbers = (total, current, max = 5) => {
    const half = Math.floor(max / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const toggleMenu = (id) => setActionMenu(actionMenu === id ? null : id);

  const confirmDelete = (id) => {
    setConfirmDeleteId(id);
    setActionMenu(null);
  };

  const updateStatus = async (id, newStatus) => {
    setWorkingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/update-programme-group-status/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update status");
      }

      setProgramGroups((prev) =>
        prev.map((pg) => (pg.id === id ? { ...pg, status: newStatus } : pg))
      );

      setToast({
        type: "success",
        message: "Status updated successfully",
      });
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status",
      });
    } finally {
      setWorkingId(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    const id = confirmDeleteId;
    if (!id) return;
    setWorkingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/delete-programme-group/${id}`,
        {
          method: "DELETE",
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete program group");
      }

      setProgramGroups((prev) => prev.filter((pg) => pg.id !== id));
      setToast({
        type: "success",
        message: "Program group deleted successfully",
      });
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to delete program group",
      });
    } finally {
      setWorkingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleEditSave = async (newName) => {
    if (!editingProgram || !newName) return;

    setWorkingId(editingProgram.id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/update-programme-group/${editingProgram.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            name: newName,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update program group");
      }

      setProgramGroups((prev) =>
        prev.map((pg) =>
          pg.id === editingProgram.id
            ? { ...pg, programme_group_name: newName }
            : pg
        )
      );
      setToast({
        type: "success",
        message: "Program group updated successfully",
      });
      setEditingProgram(null);
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to update program group",
      });
    } finally {
      setWorkingId(null);
    }
  };

  const openEditModal = (programGroup) => {
    setEditingProgram(programGroup);
    setActionMenu(null);
  };

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center p-6">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm">Loading program groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-4 bg-white shadow-sm rounded-lg p-4 border border-gray-200">
      <div className="mb-3 border-b pb-2 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">
          Programme Groups
        </h2>
        <select
          value={rowsPerPage}
          onChange={(e) => {
            setRowsPerPage(
              e.target.value === "All" ? "All" : Number(e.target.value)
            );
            setCurrentPage(1);
          }}
          className="border px-2 py-1.5 text-sm rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          {rowsPerPageOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt === "All" ? "Show all" : `${opt} per page`}
            </option>
          ))}
        </select>
      </div>

      <input
        type="text"
        placeholder="Search by program group name..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        className="w-full border px-3 py-2 text-sm rounded-lg mb-3 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
      />

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-50 text-gray-700 text-xs">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">
                Program Group
              </th>
              <th className="text-left px-4 py-3 font-semibold">Groups</th>
              <th className="text-center px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {displayedData.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-8 text-gray-500">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <Settings size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm">No program groups found.</p>
                    {searchTerm && (
                      <p className="text-xs">Try adjusting your search term.</p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {displayedData.map((pg) => (
                  <React.Fragment key={pg.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {pg.programme_group_name || "Unnamed"}
                      </td>
                      <td
                        className="px-4 py-3 text-gray-600 cursor-pointer hover:text-blue-600 text-xs"
                        onClick={() =>
                          setOpenGroupId(
                            openGroupId === pg.programme_group_id
                              ? null
                              : pg.programme_group_id
                          )
                        }
                      >
                        {pg.group_names ? "View Groups" : "No groups assigned"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <select
                          value={pg.status}
                          onChange={(e) =>
                            updateStatus(pg.id, parseInt(e.target.value))
                          }
                          disabled={workingId === pg.id}
                          className={`px-1.5 py-1 text-xs rounded font-semibold border focus:outline-none ${
                            pg.status === 1
                              ? "text-green-800 bg-green-100 border-green-400"
                              : "text-red-800 bg-red-100 border-red-400"
                          }`}
                        >
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                        </select>
                      </td>
                      <td className="relative px-4 py-3">
                        <button
                          onClick={() => toggleMenu(pg.id)}
                          className="hover:bg-gray-200 p-1.5 rounded transition-colors"
                          data-action-menu
                          disabled={workingId === pg.id}
                        >
                          {workingId === pg.id ? (
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Settings size={16} />
                          )}
                        </button>
                        {actionMenu === pg.id && (
                          <div className="absolute right-0 mt-1 w-36 bg-white border shadow-lg rounded-lg z-10 overflow-hidden">
                            <button
                              onClick={() => openEditModal(pg)}
                              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 transition-colors border-b border-gray-100"
                            >
                              <Edit size={14} className="text-blue-600" />
                              <span className="text-gray-700">Edit</span>
                            </button>
                            <button
                              onClick={() => confirmDelete(pg.id)}
                              className="w-full px-3 py-2 text-xs text-left hover:bg-red-50 text-red-600 flex items-center gap-2 transition-colors"
                            >
                              <Delete size={14} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {openGroupId === pg.programme_group_id && pg.group_names && (
                      <tr>
                        <td colSpan="4" className="bg-gray-50 px-4 py-3">
                          <div className="ml-3">
                            <h4 className="font-medium text-gray-700 text-xs mb-1.5">Groups:</h4>
                            <ul className="list-disc list-inside space-y-0.5 text-gray-600 text-xs">
                              {pg.group_names.split(", ").map((name, idx) => (
                                <li key={idx}>{name}</li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="px-2 py-1.5 text-xs rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            ⏮
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1.5 text-xs rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            ◀
          </button>
          {getPageNumbers(totalPages, currentPage).map((n) => (
            <button
              key={n}
              onClick={() => setCurrentPage(n)}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${
                n === currentPage
                  ? "bg-blue-600 text-white shadow-sm"
                  : "border hover:bg-gray-50"
              }`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1.5 text-xs rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            ▶
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-2 py-1.5 text-xs rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            ⏭
          </button>
        </div>
      )}

      <EditModal
        isOpen={editingProgram !== null}
        programGroup={editingProgram}
        onSave={handleEditSave}
        onCancel={() => setEditingProgram(null)}
        isLoading={workingId === editingProgram?.id}
      />

      <ConfirmationModal
        isOpen={confirmDeleteId !== null}
        message="Are you sure you want to delete this program group? This action cannot be undone."
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDeleteId(null)}
      />

      <div className="fixed top-3 right-3 z-50 space-y-1">
        {toast.message && (
          <div
            className={`max-w-xs w-full px-3 py-2 rounded border shadow-lg text-xs ${
              toast.type === "success"
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  toast.type === "success" ? "bg-green-400" : "bg-red-400"
                }`}
              ></div>
              {toast.message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllProgramGroups;